/**
 * Settings > Web form intake (tracker item 49, ADMIN only): turn the public service request form on or
 * off, see the form's public key, the endpoint a website posts to, a hosted form page and a copy-paste
 * HTML snippet, and regenerate the key (the old one stops working at once). Requests from the form land
 * in Requests > Service requests as "Website form", with the contact details unverified.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createAPIClient, IRequestsApi } from '../../api';
import * as types from '../../api/types';
import { getErrorMessage } from '../../utils/apiError';
import { DEFAULT_API_BASE, webFormEndpointUrl, webFormHostedUrl, webFormSnippet } from '../../utils/webForm';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from '../../components/formStyles';
import { Toast } from '../../components/Toast';

function CopyField({ id, label, value, multiline = false }: { id: string; label: string; value: string; multiline?: boolean }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }
  return (
    <div>
      <label htmlFor={id} className={labelClass}>{label}</label>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
        {multiline ? (
          <textarea id={id} readOnly className={`${inputClass} font-mono text-xs`} rows={10} value={value} onFocus={(e) => e.currentTarget.select()} />
        ) : (
          <input id={id} readOnly className={`${inputClass} min-h-[44px] font-mono text-xs`} value={value} onFocus={(e) => e.currentTarget.select()} />
        )}
        <button type="button" className={`${secondaryButtonClass} shrink-0`} onClick={() => void copy()} aria-label={`Copy ${label.toLowerCase()}`}>
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

export function WebFormIntakeSettingsPage() {
  const api = useMemo<IRequestsApi>(() => createAPIClient(), []);
  const [settings, setSettings] = useState<types.WebFormIntakeSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSettings(await api.getWebFormIntake());
    } catch (cause) {
      setError(getErrorMessage(cause, 'Could not load the web form settings'));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { void load(); }, [load]);

  async function setEnabled(enabled: boolean) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      setSettings(await api.updateWebFormIntake(enabled));
      setNotice(enabled ? 'The web form is on. Requests from it appear in Requests.' : 'The web form is off. Posts to it are refused until you turn it back on.');
    } catch (cause) {
      setError(getErrorMessage(cause, 'Could not save the web form settings'));
    } finally {
      setBusy(false);
    }
  }

  async function regenerate() {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      setSettings(await api.regenerateWebFormKey());
      setConfirmRegenerate(false);
      setNotice('A new key was generated. Update your website with the new endpoint or snippet: the old one no longer works.');
    } catch (cause) {
      setError(getErrorMessage(cause, 'Could not generate a new key'));
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6">Loading web form settings…</main>;
  if (!settings) {
    return (
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        <Link to="/settings" className="text-sm text-blue-700 hover:underline">← Settings</Link>
        {error && <Toast kind="error">{error}</Toast>}
        <p className="mt-4 text-gray-700">The web form settings could not be loaded.</p>
        <button type="button" className="mt-2 text-blue-700 underline min-h-[44px]" onClick={() => void load()}>Try again</button>
      </main>
    );
  }

  const origin = window.location.origin;
  const endpoint = settings.publicKey ? webFormEndpointUrl(settings.publicKey, process.env.REACT_APP_API_URL || DEFAULT_API_BASE, origin) : null;
  const hosted = settings.publicKey ? webFormHostedUrl(settings.publicKey, origin) : null;

  return (
    <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6">
      <div>
        <Link to="/settings" className="text-sm text-blue-700 hover:underline">← Settings</Link>
        <h1 className="text-2xl font-bold text-gray-900 mt-1">Web form intake</h1>
        <p className="mt-1 text-sm text-gray-600">
          A service request form for your own website. Anyone can fill it in without an account; each request lands in{' '}
          <Link to="/requests" className="text-blue-700 underline">Requests</Link> as &quot;Website form&quot;, where you convert it to a draft job or decline it.
        </p>
      </div>
      {error && <Toast kind="error">{error}</Toast>}
      {notice && <Toast kind="success">{notice}</Toast>}

      <section className="bg-white rounded-lg shadow p-5 space-y-4" aria-label="Web form switch">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-medium text-gray-900">The web form is <strong data-testid="web-form-state">{settings.enabled ? 'on' : 'off'}</strong></p>
            <p className="text-sm text-gray-600">
              {settings.enabled
                ? 'Your website can send requests now.'
                : 'Turned off, the form refuses every post. Turning it on the first time creates its key.'}
            </p>
          </div>
          <button type="button" className={settings.enabled ? secondaryButtonClass : primaryButtonClass} disabled={busy} onClick={() => void setEnabled(!settings.enabled)}>
            {busy ? 'Saving…' : settings.enabled ? 'Turn off' : 'Turn on'}
          </button>
        </div>
      </section>

      {settings.publicKey && endpoint && hosted && (
        <section className="bg-white rounded-lg shadow p-5 space-y-5" aria-label="Connect your website">
          <h2 className="font-semibold text-lg text-gray-900">Connect your website</h2>
          {!settings.enabled && <p role="note" className="rounded bg-amber-50 p-3 text-sm text-amber-900">The form is off: these links answer &quot;Not found&quot; until you turn it on.</p>}
          <CopyField id="web-form-key" label="Form key" value={settings.publicKey} />
          <CopyField id="web-form-endpoint" label="Endpoint (POST, JSON)" value={endpoint} />
          <div>
            <CopyField id="web-form-hosted" label="Hosted form page" value={hosted} />
            <p className="mt-1 text-xs text-gray-500">
              Link to it from your website (the snippet below embeds the form directly). <a href={hosted} target="_blank" rel="noreferrer" className="text-blue-700 underline">Open the form</a>
            </p>
          </div>
          <div>
            <CopyField id="web-form-snippet" label="HTML snippet" value={webFormSnippet(endpoint)} multiline />
            <p className="mt-1 text-xs text-gray-500">
              Paste it into a page of your website. It lists the services you mark <strong>Offer on the customer portal</strong> on{' '}
              <Link to="/settings/items" className="text-blue-700 underline">Items</Link>, and keeps a hidden field that catches spam bots.
            </p>
          </div>

          <div className="border-t pt-4">
            <h3 className="font-medium text-gray-900">Regenerate the key</h3>
            <p className="text-sm text-gray-600">Use this if the form is being abused. The old key stops working at once, so your website must be updated.</p>
            {confirmRegenerate ? (
              <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label="Confirm regenerating the key">
                <button type="button" className={primaryButtonClass} disabled={busy} onClick={() => void regenerate()}>{busy ? 'Generating…' : 'Yes, generate a new key'}</button>
                <button type="button" className={secondaryButtonClass} disabled={busy} onClick={() => setConfirmRegenerate(false)}>Cancel</button>
              </div>
            ) : (
              <button type="button" className={`${secondaryButtonClass} mt-3`} disabled={busy} onClick={() => setConfirmRegenerate(true)}>Regenerate key</button>
            )}
          </div>
        </section>
      )}

      <section className="bg-white rounded-lg shadow p-5 text-sm text-gray-700 space-y-2">
        <h2 className="font-semibold text-lg text-gray-900">What the form does</h2>
        <ul className="list-disc space-y-1 pl-5">
          <li>It asks for a name, an email address or phone number, the service address, and a service or a description; dates and time of day are optional.</li>
          <li>No customer or property is created. When you convert a request you choose (or first create) the customer and property.</li>
          <li>Nothing is emailed to the requester (their address is not verified); your company email gets the usual new-request message.</li>
          <li>Posts are limited per visitor and per form, and at most 200 website requests can wait in New at once.</li>
        </ul>
      </section>
    </main>
  );
}
